import { useEffect } from "react";
import { anchorElement, type PositionAnchor } from "./anchor.js";
import { wasTopLayerAtEventStart } from "./layer-stack.js";

export interface UseDismissableOptions {
  /** Layer id from useLayer — dismissal fires only if this layer was topmost
   *  when the press STARTED (Esc included, unless
   *  {@link UseDismissableOptions.escapeRequiresTopLayer} says otherwise). */
  layerId: string;
  /** Active only while open. */
  open: boolean;
  onDismiss: (reason: "escape" | "outside-press") => void;
  /** Elements considered "inside" — presses within them never dismiss. A
   *  virtual anchor rect has no node, so it is never inside. */
  insideRefs: ReadonlyArray<React.RefObject<PositionAnchor | null>>;
  escape?: boolean;
  /**
   * Whether Esc acts only while this layer is TOPMOST. Default true, which is
   * right for anything that owns the key while it is up — a dialog, a popover,
   * a select.
   *
   * A TRANSIENT layer sets this false. A tooltip owns no focus and traps
   * nothing; it is a reading that appears beside whatever the pointer or the
   * keyboard is on, and more than one can be up at once — hover one element
   * while another holds focus and there are two. Under top-layer gating one Esc
   * cleared exactly one of them and left the other on screen, which is the
   * defect a browser pass caught on the LAMA calendar: click a day (the pointer
   * stays on it), arrow to the next (that one takes focus), press Esc, and a
   * tooltip is still there. A layer that does not own the key also does not
   * CONSUME it — see the handler.
   */
  escapeRequiresTopLayer?: boolean;
  outsidePress?: boolean;
  /** A modal must prevent the outside press from taking focus after close. */
  preventOutsideFocus?: boolean;
}

/**
 * The ONE implementation of Esc + outside-press dismissal (strategy §14.3
 * `useDismissable`). Replaces four divergent copies (Popover, Select,
 * DatePicker, dialog scrims). Nesting-aware: consults the layer stack and
 * acts only when its layer was topmost as the event STARTED, so stacked
 * overlays unwind one per Esc with no stopPropagation games — even when the
 * layer above closes in an earlier listener of the same press (SHR-BUG-7).
 */
export function useDismissable({
  layerId,
  open,
  onDismiss,
  insideRefs,
  escape = true,
  escapeRequiresTopLayer = true,
  outsidePress = true,
  preventOutsideFocus = false,
}: UseDismissableOptions): void {
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (!escape || e.key !== "Escape") return;
      // Every layer counts here, so a tooltip above a dialog wins the key.
      const top = wasTopLayerAtEventStart(e, layerId);
      if (!top && escapeRequiresTopLayer) return;
      /* CONSUMED ONLY BY A LAYER THAT OWNS THE KEY. A transient reading
         dismisses itself without consuming Escape, even when topmost, so the
         focused control or canvas can still answer the same press. */
      if (top && escapeRequiresTopLayer) e.preventDefault();
      onDismiss("escape");
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!outsidePress) return;
      /* A tooltip above this layer does not cover it: it takes no pointer
         input, so one press outside still closes the menu under it. Esc keeps
         its top-layer rule, so the first Esc still clears the tooltip. */
      if (!wasTopLayerAtEventStart(e, layerId, { ignoreTooltips: true }))
        return;
      const t = e.target as Node;
      for (const r of insideRefs) {
        if (anchorElement(r.current)?.contains(t)) return;
      }
      if (preventOutsideFocus) e.preventDefault();
      onDismiss("outside-press");
    };

    document.addEventListener("keydown", onKey);
    // `pointerdown` (not mousedown) in the CAPTURE phase. Two reasons:
    //  1. Capture fires document→target, BEFORE any descendant can
    //     `stopPropagation()` — a bubble listener misses presses inside subtrees
    //     that stop it (e.g. the xyflow canvas pane, which swallows pointer
    //     events for panning), so the overlay would never dismiss there.
    //  2. pointerdown fires even when the target `preventDefault()`s it (the
    //     canvas pane does, for panning) — that suppresses the compat `mousedown`
    //     entirely, so a mousedown listener would never see a pane press at all.
    //  Also covers touch/pen. (Radix/Floating-UI use pointerdown for the same.)
    //  Non-modal layers use a passive listener; modals prevent the outside press
    //  from stealing focus after the focus trap restores the opener.
    document.addEventListener("pointerdown", onPointerDown, {
      capture: true,
      passive: !preventOutsideFocus,
    });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
    };
    // insideRefs is intentionally treated as stable (callers pass ref objects,
    // not fresh arrays of values); the array identity may change per render,
    // which is harmless — listeners re-bind cheaply.
  }, [
    open,
    escape,
    escapeRequiresTopLayer,
    outsidePress,
    preventOutsideFocus,
    layerId,
    onDismiss,
    insideRefs,
  ]);
}
